import { Alert, styled, useTheme } from '@mui/material';
import { CustomContentProps, SnackbarProvider as NotistackSnackbarProvider } from 'notistack';
import React from 'react';
import { CheckCircle1AlternateIcon, ErrorIconBold, InformationCircleIconBold } from 'src/icons';

const StyledSnackbarProvider = styled(NotistackSnackbarProvider)({
    minWidth: '0px',
});

const SnackbarAlert = styled(Alert)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    alignItems: 'center',
    '& .MuiAlert-message': {
        fontWeight: 600,
        padding: theme.spacing(1, 0),
        lineHeight: 1.25,
    },
    '& .MuiAlert-icon': {
        marginRight: theme.spacing(1),
        opacity: 1,
    },
    '& .MuiAlert-action': {
        alignItems: 'center',
        padding: theme.spacing(0, 0, 0, 2),
        marginRight: 0,
    },
}));

const SnackbarContent = React.forwardRef<HTMLDivElement, CustomContentProps>(
    ({ id, message, style, variant, action }, ref) => {
        const theme = useTheme();
        return (
            <SnackbarAlert
                style={style}
                severity={variant === 'default' ? 'info' : variant}
                variant="filled"
                ref={ref}
                iconMapping={{
                    error: (
                        <ErrorIconBold height="18px" fill={theme.resqPalette.icon.onHighlight} />
                    ),
                    info: (
                        <InformationCircleIconBold
                            height="18px"
                            fill={theme.resqPalette.icon.onHighlight}
                        />
                    ),
                    success: (
                        <CheckCircle1AlternateIcon
                            height="18px"
                            fill={theme.resqPalette.icon.onHighlight}
                        />
                    ),
                }}
                action={typeof action === 'function' ? action(id) : action}
            >
                {message}
            </SnackbarAlert>
        );
    },
);

interface SnackbarProviderProps {
    children: React.ReactNode;
}

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
    return (
        <StyledSnackbarProvider
            dense
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            Components={{
                success: SnackbarContent,
                error: SnackbarContent,
                warning: SnackbarContent,
                info: SnackbarContent,
                default: SnackbarContent,
            }}
        >
            {children}
        </StyledSnackbarProvider>
    );
};
